import { createUuid } from "../../../lib/trace/context";
import type { ItineraryEditor, ItineraryNode, ItinerarySegment, RailwayTransferAlternative, RailwayTransferContext, RailwayTransferGroup, RailwayTransferPlan, TimeSlot, TransportSelection } from "../types";
import { rebuildAdjacentSegments, validateEditor } from "./itineraryEditorService";
import { calculatedTransportDurationMinutes } from "./itineraryTransportTime";

const slots: TimeSlot[] = ["morning", "afternoon", "evening"];
const changedChainMessage = "中转节点或交通信息已改变，无法直接替换整条路线；仍可查看主备或逐段编辑交通";

/** 待重查事实随草稿保存；不能只依赖不会落库的临时时段标记。 */
export function isRailwayTransferTimetablePending(segment: ItinerarySegment): boolean {
  return segment.transportMode === "rail" && Boolean(segment.sourcePayload?.railwayTransfer)
    && (segment.sourceType === "pending" || !segment.sourcePayload?.selected);
}

/** 统一持久化空值、金额与时刻表示，避免合法保存往返被误判为人工编辑。 */
function canonical(value: unknown, key = ""): unknown {
  if (value == null) return null;
  if (typeof value === "string") {
    if (/At$/.test(key) && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      const zoned = /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}Z`;
      return new Date(zoned).getTime();
    }
    if (["price", "distanceKm", "tollsYuan", "tollDistanceKm"].includes(key)) return Number(value);
  }
  if (Array.isArray(value)) return value.map((item) => canonical(item));
  if (typeof value === "object") return Object.fromEntries(Object.entries(value)
    .filter(([, item]) => item != null).sort(([left], [right]) => left.localeCompare(right))
    .map(([name, item]) => [name, canonical(item, name)]));
  return value;
}

/** 路线段距离落库为 DECIMAL(10,2)，十进制移位避免浮点乘法误判舍入边界。 */
function persistedDistance(value: unknown): number | null {
  if (value == null) return null;
  const number = Number(value);
  if (!Number.isFinite(number)) throw new Error(changedChainMessage);
  const [digits, exponent = "0"] = String(number).split("e");
  return Math.round(Number(`${digits}e${Number(exponent) + 2}`)) / 100;
}

/** 已保存的旧签名也按距离列精度比较；JSON 内的查询与人工事实保持原精度。 */
function normalizedStoredChainSignature(signature: string): string {
  const snapshot = JSON.parse(signature) as { segments: Record<string, unknown>[] };
  return JSON.stringify(canonical({ ...snapshot, segments: snapshot.segments.map((segment) => ({
    ...segment, distanceKm: persistedDistance(segment.distanceKm),
  })) }));
}

/** 仅签入真实保存字段及组引用，不把只读主数据展示字段写进并发边界。 */
function chainSignature(editor: ItineraryEditor, group: RailwayTransferGroup): string {
  const nodes = group.nodeIds.map((id) => {
    const day = editor.days.find((item) => item.nodes.some((node) => node.id === id));
    const node = day?.nodes.find((item) => item.id === id);
    if (!day || !node) throw new Error(changedChainMessage);
    return { dayId: day.id, dayDate: day.dayDate, nodeIndex: day.nodes.indexOf(node),
      id: node.id, timeSlot: node.timeSlot, sortOrder: node.sortOrder, nodeType: node.nodeType,
      pointSpuId: node.pointSpuId, stayAreaId: node.stayAreaId, organizationSiteId: node.organizationSiteId,
      // 火车站标题由后端主数据/历史快照归一，实际身份由类型和资源引用决定。
      title: node.nodeType === "railway_station" && node.pointSpuId ? null : node.title,
      description: node.description, planStartAt: node.planStartAt, planEndAt: node.planEndAt };
  });
  const segments = group.segmentIds.map((id) => {
    const segment = editor.segments.find((item) => item.id === id);
    if (!segment) throw new Error(changedChainMessage);
    const payload = segment.sourcePayload;
    return { id: segment.id, startDayId: segment.startDayId, endDayId: segment.endDayId,
      routeSeq: segment.routeSeq, majorId: segment.majorId, routeType: segment.routeType, transportMode: segment.transportMode,
      fromNodeId: segment.fromNodeId, toNodeId: segment.toNodeId,
      transportSpuId: segment.transportSpuId, transportSkuId: segment.transportSkuId, busSpuId: segment.busSpuId, busSkuId: segment.busSkuId,
      routeCode: segment.routeCode, departureAt: segment.departureAt, arrivalAt: segment.arrivalAt,
      distanceKm: persistedDistance(segment.distanceKm), durationMinutes: segment.durationMinutes, segmentDescription: segment.segmentDescription,
      sourceType: segment.sourceType, remark: segment.remark,
      sourcePayload: payload ? { version: payload.version, selected: payload.selected, backups: payload.backups, toolResultSnapshot: payload.toolResultSnapshot } : null };
  });
  const memberships = editor.days.flatMap((day) => (day.parallelTracks?.tracks ?? [])
    .filter((track) => track.segmentIds.some((id) => group.segmentIds.includes(id))
      || track.nodeIds.some((id) => group.generatedNodeIds.includes(id)))
    .map((track) => ({ dayId: day.id, trackId: track.id, majorId: track.majorId, nodeIds: track.nodeIds, segmentIds: track.segmentIds })));
  return JSON.stringify(canonical({ nodes, segments, memberships }));
}

/** 收拢生成链前必须验证全部节点、区间和关联事实，避免丢失后续人工修改。 */
function assertRailwayTransferReplaceable(editor: ItineraryEditor, group: RailwayTransferGroup, segmentId: string, allowPending = false): void {
  if (group.plans.length === 0 || group.nodeIds.length !== group.segmentIds.length + 1
    || JSON.stringify(group.nodeIds.slice(1, -1)) !== JSON.stringify(group.generatedNodeIds)
    || new Set(group.nodeIds).size !== group.nodeIds.length || new Set(group.segmentIds).size !== group.segmentIds.length
    || !group.segmentIds.includes(segmentId)) throw new Error(changedChainMessage);
  for (const [index, id] of group.segmentIds.entries()) {
    const member = editor.segments.find((item) => item.id === id);
    if (!allowPending && member && isRailwayTransferTimetablePending(member)) throw new Error(changedChainMessage);
    if (!member || member.fromNodeId !== group.nodeIds[index] || member.toNodeId !== group.nodeIds[index + 1]
      || (member.transportMode === "rail" && JSON.stringify(canonical(member.sourcePayload?.railwayTransfer)) !== JSON.stringify(canonical(group)))) {
      throw new Error(changedChainMessage);
    }
  }
  if (editor.segments.some((item) => !group.segmentIds.includes(item.id)
    && (group.generatedNodeIds.includes(item.fromNodeId) || group.generatedNodeIds.includes(item.toNodeId)))) throw new Error(changedChainMessage);
  if (chainSignature(editor, group) !== normalizedStoredChainSignature(group.chainSignature)) throw new Error(changedChainMessage);
}

/** 任一火车腿恢复整组主备；替换受限时保留展示，并单独返回明确原因。 */
export function resolveRailwayTransferContext(editor: ItineraryEditor, segmentId: string): RailwayTransferContext | null {
  const group = editor.segments.find((item) => item.id === segmentId)?.sourcePayload?.railwayTransfer;
  if (!group || group.plans.length === 0) return null;
  const nodes = editor.days.flatMap((day) => day.nodes);
  const fromNode = nodes.find((node) => node.id === group.nodeIds[0]);
  const toNode = nodes.find((node) => node.id === group.nodeIds.at(-1));
  if (!fromNode || !toNode) throw new Error(changedChainMessage);
  let replaceError: string | undefined;
  try { assertRailwayTransferReplaceable(editor, group, segmentId); }
  catch { replaceError = changedChainMessage; }
  return structuredClone({ groupId: group.groupId, plan: { ...group.plans[0], backups: group.plans.slice(1) },
    fromNode, toNode, segmentIds: group.segmentIds, replaceError });
}

/** 安全收拢完整生成链供既有工作区应用入口重用，原首末节点与第一段身份不变。 */
export function collapseRailwayTransfer(editor: ItineraryEditor, segmentId: string): { editor: ItineraryEditor; segment: ItinerarySegment } {
  const context = resolveRailwayTransferContext(editor, segmentId);
  if (!context) throw new Error("当前区间没有可恢复的中转组");
  if (context.replaceError) throw new Error(context.replaceError);
  const group = editor.segments.find((item) => item.id === segmentId)!.sourcePayload!.railwayTransfer!;
  const next = structuredClone(editor);
  const first = next.segments.find((item) => item.id === group.segmentIds[0])!;
  const last = next.segments.find((item) => item.id === group.segmentIds.at(-1))!;
  const replacement: ItinerarySegment = { ...first, toNodeId: last.toNodeId, endDayId: last.endDayId,
    routeCode: null, departureAt: null, arrivalAt: null, durationMinutes: null, distanceKm: null,
    transportSpuId: null, transportSkuId: null, busSpuId: null, busSkuId: null, sourceType: "pending", sourcePayload: null };
  next.segments = next.segments.filter((item) => !group.segmentIds.includes(item.id) || item.id === first.id)
    .map((item) => item.id === first.id ? replacement : (item.majorId ?? null) === (first.majorId ?? null) && item.routeSeq > last.routeSeq
      ? { ...item, routeSeq: item.routeSeq - group.segmentIds.length + 1 } : item);
  for (const day of next.days) {
    day.nodes = day.nodes.filter((node) => !group.generatedNodeIds.includes(node.id));
    for (const slot of slots) day.nodes.filter((node) => node.timeSlot === slot).forEach((node, index) => { node.sortOrder = index + 1; });
    for (const track of day.parallelTracks?.tracks ?? []) {
      if (!track.segmentIds.some((id) => group.segmentIds.includes(id))) continue;
      const index = track.segmentIds.indexOf(first.id);
      const nodeIndex = track.nodeIds.indexOf(first.fromNodeId);
      if (JSON.stringify(track.segmentIds.slice(index, index + group.segmentIds.length)) !== JSON.stringify(group.segmentIds)
        || JSON.stringify(track.nodeIds.slice(nodeIndex, nodeIndex + group.nodeIds.length)) !== JSON.stringify(group.nodeIds)) throw new Error(changedChainMessage);
      track.segmentIds.splice(index, group.segmentIds.length, first.id);
      track.nodeIds.splice(nodeIndex, group.nodeIds.length, first.fromNodeId, last.toNodeId);
    }
  }
  return { editor: next, segment: replacement };
}

/** 班次身份不包含席别与报价，选价变化无需重新生成站点及接驳。 */
function railwayJourneyIdentity(leg: TransportSelection): string {
  return JSON.stringify(canonical({ provider: leg.provider, routeCode: leg.routeCode,
    fromStationId: leg.fromStationId, toStationId: leg.toStationId,
    departureAt: leg.departureAt, arrivalAt: leg.arrivalAt }));
}

/** 同班次原位更新选价与整条备选，保留已测算接驳与全部人工事实。 */
export function updateRailwayTransferAlternatives(editor: ItineraryEditor, segmentId: string, plan: RailwayTransferPlan): ItineraryEditor | null {
  const group = editor.segments.find((segment) => segment.id === segmentId)?.sourcePayload?.railwayTransfer;
  if (group && editor.segments.some((segment) => group.segmentIds.includes(segment.id) && isRailwayTransferTimetablePending(segment))) {
    throw new Error("中转车次正在按调整后的时段重查，请等待完成后再保存路线");
  }
  const previousLegs = group?.plans[0]?.legs;
  if (!group || previousLegs?.length !== 2 || plan.legs.length !== 2
    || previousLegs.some((leg, index) => railwayJourneyIdentity(leg) !== railwayJourneyIdentity(plan.legs[index]))) return null;
  const alternatives = validatedTransferAlternatives(plan);
  const selectionChanged = JSON.stringify(canonical(previousLegs)) !== JSON.stringify(canonical(plan.legs));
  const railIds = [group.segmentIds[0], group.segmentIds.at(-1)];
  let refreshSignature = false;
  {
    const nodes = editor.days.flatMap((day) => day.nodes);
    for (const [index, id] of railIds.entries()) {
      const segment = editor.segments.find((item) => item.id === id);
      const leg = previousLegs[index];
      const from = nodes.find((node) => node.id === segment?.fromNodeId);
      const to = nodes.find((node) => node.id === segment?.toNodeId);
      if (!segment || segment.transportMode !== "rail" || segment.sourcePayload?.railwayTransfer?.groupId !== group.groupId
        || JSON.stringify(canonical(segment.sourcePayload.selected)) !== JSON.stringify(canonical(leg))
        || railwayJourneyIdentity({ ...leg, routeCode: segment.routeCode, departureAt: segment.departureAt, arrivalAt: segment.arrivalAt }) !== railwayJourneyIdentity(leg)
        || from?.nodeType !== "railway_station" || from.pointSpuId !== leg.fromStationId
        || to?.nodeType !== "railway_station" || to.pointSpuId !== leg.toStationId) throw new Error(changedChainMessage);
    }
    try { assertRailwayTransferReplaceable(editor, group, segmentId); refreshSignature = selectionChanged; }
    catch { /* 原有人工改动允许保留，但不能因更新席别解除整链替换保护。 */ }
  }
  const next = structuredClone(editor);
  if (selectionChanged) for (const [index, id] of railIds.entries()) {
    const payload = next.segments.find((segment) => segment.id === id)!.sourcePayload!;
    payload.selected = structuredClone(plan.legs[index]);
    payload.toolResultSnapshot = { ...structuredClone(plan.snapshot), queryKey: `${plan.snapshot.queryKey}|leg:${index}`,
      options: [structuredClone(plan.snapshot.options[index] ?? plan.legs[index])] };
  }
  const updated = { ...structuredClone(group), plans: structuredClone(alternatives),
    chainSignature: refreshSignature ? chainSignature(next, group) : group.chainSignature };
  for (const segment of next.segments) {
    if (group.segmentIds.includes(segment.id) && segment.sourcePayload?.railwayTransfer?.groupId === group.groupId) {
      segment.sourcePayload.railwayTransfer = structuredClone(updated);
    }
  }
  return next;
}

/** 只比较组的节点资源、日期、真实顺序与专业路径，不把同日时段调整误判为路径拆改。 */
function sameTransferTopology(before: ItineraryEditor, after: ItineraryEditor, group: RailwayTransferGroup, ignoreDisplayOrder = false): boolean {
  const shape = (editor: ItineraryEditor) => {
    const snapshot = JSON.parse(chainSignature(editor, group)) as {
      nodes: Record<string, unknown>[]; segments: Record<string, unknown>[]; memberships: unknown;
    };
    const keys = ["id", "startDayId", "endDayId", "fromNodeId", "toNodeId", "routeSeq", "majorId", "routeType", "transportMode"];
    return { nodes: snapshot.nodes.map((node) => Object.fromEntries(Object.entries(node)
      .filter(([key]) => !["timeSlot", "sortOrder", "planStartAt", "planEndAt", ...(ignoreDisplayOrder ? ["nodeIndex"] : [])].includes(key)))),
    segments: snapshot.segments.map((segment) => Object.fromEntries(keys.map((key) => [key, segment[key]]))), memberships: snapshot.memberships };
  };
  try { return JSON.stringify(shape(before)) === JSON.stringify(shape(after)); }
  catch { return false; }
}

/** 两条铁路始终引用相同组；待重查段只保留组信息，不恢复失效车次事实。 */
function attachTransferGroup(editor: ItineraryEditor, group: RailwayTransferGroup): void {
  for (const segment of editor.segments) {
    if (!group.segmentIds.includes(segment.id) || segment.transportMode !== "rail") continue;
    segment.sourcePayload = { version: 1, backups: [], ...segment.sourcePayload, railwayTransfer: structuredClone(group) };
  }
}

/** 新插入完整中转的日界拆分后固定基线，保留整条主备恢复能力。 */
export function refreshInsertedRailwayTransfer(editor: ItineraryEditor, segmentIds: string[]): ItineraryEditor {
  const next = structuredClone(editor);
  const groups = new Map<string, RailwayTransferGroup>();
  for (const segment of next.segments) {
    const group = segment.sourcePayload?.railwayTransfer;
    if (group && segmentIds.includes(segment.id)) groups.set(group.groupId, group);
  }
  for (const group of groups.values()) attachTransferGroup(next, { ...group, chainSignature: chainSignature(next, group) });
  return next;
}

/** 仅同日且完整路径未变的时段移动维护组基线；原有人工修改继续保持替换保护。 */
export function preserveRailwayTransferAfterSlotMove(before: ItineraryEditor, after: ItineraryEditor, nodeId: string): ItineraryEditor {
  const groups = new Map<string, RailwayTransferGroup>();
  for (const segment of before.segments) {
    const group = segment.sourcePayload?.railwayTransfer;
    if (group?.nodeIds.includes(nodeId)) groups.set(group.groupId, group);
  }
  if (!groups.size) return after;
  const next = structuredClone(after);
  for (const group of groups.values()) {
    if (!sameTransferTopology(before, next, group, true)) continue;
    attachTransferGroup(next, group);
    try { assertRailwayTransferReplaceable(before, group, group.segmentIds[0], true); }
    catch { continue; }
    const expected = structuredClone(before);
    for (const day of expected.days) for (const node of day.nodes) {
      const changed = next.days.flatMap((item) => item.nodes).find((item) => item.id === node.id);
      if (changed && group.nodeIds.includes(node.id)) Object.assign(node, { timeSlot: changed.timeSlot, sortOrder: changed.sortOrder });
    }
    for (const day of expected.days) {
      const order = next.days.find((item) => item.id === day.id)!.nodes.map((node) => node.id);
      day.nodes.sort((left, right) => order.indexOf(left.id) - order.indexOf(right.id));
    }
    expected.segments = expected.segments.map((segment) => group.segmentIds.includes(segment.id)
      && segment.fromNodeId === nodeId && segment.transportMode === "rail"
      ? next.segments.find((item) => item.id === segment.id)! : segment);
    if (chainSignature(expected, group) !== chainSignature(next, group)) continue;
    attachTransferGroup(next, { ...group, chainSignature: chainSignature(next, group) });
  }
  return next;
}

/** 自动重查在发起时锁住全组事实与主备；非相邻第一腿的编辑也不能被迟到结果洗掉。 */
export function railwayTransferAutomaticBaseline(editor: ItineraryEditor, segmentId: string): string | null {
  const group = editor.segments.find((segment) => segment.id === segmentId)?.sourcePayload?.railwayTransfer;
  if (!group) return null;
  try { return JSON.stringify([chainSignature(editor, group), canonical(group)]); }
  catch { return null; }
}

/** 第二腿可能属于次日，自动查询沿用该腿实际出发日，而非整组所在日容器。 */
export function railwayTransferDepartureDate(segment: ItinerarySegment): string | undefined {
  const group = segment.sourcePayload?.railwayTransfer;
  const index = group?.segmentIds[0] === segment.id ? 0 : group?.segmentIds.at(-1) === segment.id ? 1 : -1;
  return index >= 0 ? group?.plans[0]?.legs[index]?.departureAt?.slice(0, 10) : undefined;
}

/** 受控时段重查只刷新实际选中的一腿，整条备选不混搭；查询来源仍保留在各铁路段。 */
export function applyRailwayTransferAutomaticTimetable(before: ItineraryEditor, after: ItineraryEditor, segmentId: string): ItineraryEditor {
  const previous = before.segments.find((segment) => segment.id === segmentId);
  const group = previous?.sourcePayload?.railwayTransfer;
  if (!group || !previous || !isRailwayTransferTimetablePending(previous)) return after;
  const next = structuredClone(after);
  attachTransferGroup(next, group);
  try { assertRailwayTransferReplaceable(before, group, segmentId, true); }
  catch { return next; }
  if (!sameTransferTopology(before, next, group)) return next;
  const railIds = [group.segmentIds[0], group.segmentIds.at(-1)!];
  const railSegments = railIds.map((id) => next.segments.find((segment) => segment.id === id)!);
  const stillPending = railSegments.some(isRailwayTransferTimetablePending);
  const legs = railSegments.map((segment, index) => segment.sourcePayload?.selected ?? group.plans[0].legs[index]);
  const changedIndex = railIds.indexOf(segmentId);
  const changed = railSegments[changedIndex];
  const snapshot = changed?.sourcePayload?.toolResultSnapshot;
  const actual = legs[changedIndex];
  const original = snapshot?.options.find((option) => option.optionId === actual.optionId && option.routeCode === actual.routeCode
    && option.fromStationId === actual.fromStationId && option.toStationId === actual.toStationId
    && option.departureAt?.slice(11, 19) === actual.departureAt?.slice(11, 19));
  if (!snapshot || !original?.departureAt || !actual.departureAt
    || legs.some((leg, index) => leg.departureAt?.slice(0, 10) !== group.plans[0].legs[index].departureAt?.slice(0, 10)
      || leg.arrivalAt?.slice(0, 10) !== group.plans[0].legs[index].arrivalAt?.slice(0, 10))) return next;
  const offset = Math.round((Date.parse(`${original.departureAt.slice(0, 10)}T00:00:00Z`) - Date.parse(`${actual.departureAt.slice(0, 10)}T00:00:00Z`)) / 86_400_000);
  const shift = (value?: string | null) => value
    ? `${new Date(Date.parse(`${value.slice(0, 10)}T00:00:00Z`) + offset * 86_400_000).toISOString().slice(0, 10)}${value.slice(10)}` : value;
  const plan: RailwayTransferPlan = { legs: structuredClone(legs), backups: structuredClone(group.plans.slice(1)),
    snapshot: { ...structuredClone(snapshot), queryKey: `derived-transfer:${group.groupId}`,
      options: legs.map((leg) => ({ ...structuredClone(leg), departureAt: shift(leg.departureAt), arrivalAt: shift(leg.arrivalAt) })),
      isReference: railSegments.some((segment) => segment.sourcePayload?.toolResultSnapshot?.isReference),
      warnings: [...new Set([...railSegments.flatMap((segment) => segment.sourcePayload?.toolResultSnapshot?.warnings ?? []),
        "已按调整后的时段更新车次，当前中转由两段实际车次组合，请核对换乘时间"])] } };
  try { if (!stillPending) validatedTransferAlternatives(plan); }
  catch { return next; }
  const expected = structuredClone(before);
  expected.segments = expected.segments.map((segment) => segment.id === segmentId ? structuredClone(changed) : segment);
  for (const day of expected.days) for (const node of day.nodes) {
    const current = next.days.flatMap((item) => item.nodes).find((item) => item.id === node.id);
    if (current && group.nodeIds.includes(node.id)) Object.assign(node, { timeSlot: current.timeSlot, sortOrder: current.sortOrder });
  }
  if (chainSignature(expected, group) !== chainSignature(next, group)) return next;
  const oldLeg = group.plans[0].legs[changedIndex];
  for (const [id, field, oldTime, newTime] of [
    [changed.fromNodeId, "planEndAt", oldLeg.departureAt, actual.departureAt],
    [changed.toNodeId, "planStartAt", oldLeg.arrivalAt, actual.arrivalAt],
  ] as const) {
    const node = next.days.flatMap((day) => day.nodes).find((item) => item.id === id);
    if (node && group.generatedNodeIds.includes(id) && canonical(node[field], field) === canonical(oldTime, field)) node[field] = newTime;
  }
  attachTransferGroup(next, { ...group, plans: [{ legs: plan.legs, snapshot: plan.snapshot }, ...group.plans.slice(1)],
    chainSignature: chainSignature(next, group) });
  return next;
}

/** 自动测算只为此前完整的组刷新事实基线；手工交通编辑不调用此入口。 */
export function applyRailwayTransferAutomaticMeasurement(editor: ItineraryEditor, measuredSegment: ItinerarySegment): ItineraryEditor {
  const next = structuredClone(editor);
  const index = next.segments.findIndex((segment) => segment.id === measuredSegment.id);
  if (index < 0) return editor;
  next.segments[index] = structuredClone(measuredSegment);
  const groups = new Map<string, RailwayTransferGroup>();
  for (const segment of editor.segments) {
    const group = segment.sourcePayload?.railwayTransfer;
    if (!group || !group.segmentIds.includes(measuredSegment.id) || groups.has(group.groupId)) continue;
    try { assertRailwayTransferReplaceable(editor, group, segment.id, true); }
    catch { continue; }
    const updated = { ...structuredClone(group), chainSignature: chainSignature(next, group) };
    groups.set(group.groupId, updated);
  }
  for (const segment of next.segments) {
    const group = segment.sourcePayload?.railwayTransfer;
    const updated = group && groups.get(group.groupId);
    if (updated && segment.sourcePayload) segment.sourcePayload.railwayTransfer = structuredClone(updated);
  }
  return next;
}

/** 校验主备每项均为可应用的完整两腿班次，避免分腿混搭写入。 */
function validatedTransferAlternatives(plan: RailwayTransferPlan): RailwayTransferAlternative[] {
  const alternatives = [{ legs: plan.legs, snapshot: plan.snapshot }, ...(plan.backups ?? [])];
  for (const alternative of alternatives) {
    if (alternative.legs.length !== 2) throw new Error("请选择完整的两段中转火车方案");
    for (const leg of alternative.legs) {
      if (leg.provider !== "py12306" || !leg.fromStationId || !leg.toStationId || !leg.fromName || !leg.toName) {
        throw new Error("中转方案有车站未匹配启用主数据，请维护车站后重新查询");
      }
      const minutes = calculatedTransportDurationMinutes(leg.departureAt, leg.arrivalAt);
      if (minutes === null || minutes <= 0 || !leg.routeCode) throw new Error("中转班次时刻不完整，请重新查询");
    }
    if (calculatedTransportDurationMinutes(alternative.legs[0].arrivalAt, alternative.legs[1].departureAt) === null) {
      throw new Error("中转班次的到发顺序无效，请重新查询");
    }
  }
  return alternatives;
}

/** 在工作区基线校验完成后，将已确认中转原子展开为普通节点和区间。 */
export function expandRailwayTransfer(editor: ItineraryEditor, segmentId: string, plan: RailwayTransferPlan, placeInsertedStations = false): ItineraryEditor {
  const original = editor.segments.find((segment) => segment.id === segmentId);
  if (!original || original.transportMode !== "rail" || plan.legs.length !== 2) throw new Error("请选择完整的两段中转火车方案");
  const alternatives = validatedTransferAlternatives(plan);
  const [first, last] = plan.legs;
  const wait = calculatedTransportDurationMinutes(first.arrivalAt, last.departureAt);
  if (wait === null || wait < 0) throw new Error("中转班次的到发顺序无效，请重新查询");
  const next = structuredClone(editor);
  const fromDay = next.days.find((day) => day.id === original.startDayId);
  const toDay = next.days.find((day) => day.id === original.endDayId);
  const origin = fromDay?.nodes.find((node) => node.id === original.fromNodeId);
  const destination = toDay?.nodes.find((node) => node.id === original.toNodeId);
  if (!fromDay || !toDay || !origin || !destination
    || origin.pointSpuId !== first.fromStationId || destination.pointSpuId !== last.toStationId) {
    throw new Error("中转方案实际车站与当前路线不一致，请重新选择");
  }
  const sameStation = first.toStationId === last.fromStationId;
  // 跨日参考班次仍沿用已有日程容器；不因查询结果自行创建或迁移行程日。
  const sameCalendarDay = first.departureAt?.slice(0, 10) === last.arrivalAt?.slice(0, 10);
  const slotFor = (value: string | null | undefined): TimeSlot => {
    if (placeInsertedStations && fromDay.id === toDay.id && value) {
      const hour = Number(value.slice(11, 13));
      const proposed = value.slice(0, 10) !== first.departureAt?.slice(0, 10) ? 2
        : hour < 12 ? 0 : hour < 18 ? 1 : 2;
      // 首末归位因既有连接冲突回退时，中间站一并保留原安排，不能只移动半条链。
      return slots[Math.max(slots.indexOf(origin.timeSlot), Math.min(slots.indexOf(destination.timeSlot), proposed))];
    }
    if (!sameCalendarDay || fromDay.id !== toDay.id) return origin.timeSlot;
    const hour = Number(value?.slice(11, 13));
    const proposed = hour < 12 ? 0 : hour < 18 ? 1 : 2;
    return slots[Math.max(slots.indexOf(origin.timeSlot), Math.min(slots.indexOf(destination.timeSlot), proposed))];
  };
  const station = (name: string, ref: string, arrival: string | null | undefined, departure: string | null | undefined): ItineraryNode => ({
    id: createUuid(), nodeType: "railway_station", title: name, pointSpuId: ref, stayAreaId: null, organizationSiteId: null,
    timeSlot: slotFor(arrival ?? departure), sortOrder: 0, planStartAt: arrival ?? null, planEndAt: departure ?? null,
  });
  const arrivalStation = station(first.toName!, first.toStationId!, first.arrivalAt, sameStation ? last.departureAt : null);
  const departureStation = sameStation ? arrivalStation : station(last.fromName!, last.fromStationId!, null, last.departureAt);
  const inserted = sameStation ? [arrivalStation] : [arrivalStation, departureStation];

  fromDay.nodes.sort((a, b) => slots.indexOf(a.timeSlot) - slots.indexOf(b.timeSlot) || a.sortOrder - b.sortOrder);
  fromDay.nodes.splice(fromDay.nodes.findIndex((node) => node.id === origin.id) + 1, 0, ...inserted);
  for (const slot of slots) fromDay.nodes.filter((node) => node.timeSlot === slot).forEach((node, index) => { node.sortOrder = index + 1; });

  const rail = (leg: TransportSelection, fromId: string, toId: string, index: number): ItinerarySegment => ({
    ...structuredClone(original), id: index === 0 ? original.id : createUuid(),
    startDayId: fromDay.id, endDayId: index === 0 ? fromDay.id : toDay.id,
    fromNodeId: fromId, toNodeId: toId, routeType: "main_transport", transportMode: "rail",
    routeCode: leg.routeCode, departureAt: leg.departureAt, arrivalAt: leg.arrivalAt,
    durationMinutes: calculatedTransportDurationMinutes(leg.departureAt, leg.arrivalAt), distanceKm: null,
    transportSpuId: null, transportSkuId: null, busSpuId: null, busSkuId: null,
    segmentDescription: index === 0 ? original.segmentDescription : null, remark: index === 0 ? original.remark : null,
    sourceType: "py12306", sourcePayload: { version: 1, selected: structuredClone(leg), backups: [], toolResultSnapshot: {
      ...structuredClone(plan.snapshot), queryKey: `${plan.snapshot.queryKey}|leg:${index}`,
      options: [structuredClone(plan.snapshot.options[index] ?? leg)],
    } },
  });
  const chain = [rail(first, origin.id, arrivalStation.id, 0)];
  if (!sameStation) chain.push({
    id: createUuid(), startDayId: fromDay.id, endDayId: fromDay.id, routeSeq: original.routeSeq,
    majorId: original.majorId, fromNodeId: arrivalStation.id, toNodeId: departureStation.id,
    routeType: "charter_bus", transportMode: "bus",
    // 选择中转明确指定异站接驳为大巴；manual 保留方式，缺少测算事实仍显示待确认。
    sourceType: "manual", sourcePayload: null,
  });
  chain.push(rail(last, departureStation.id, destination.id, 1));
  // 显式专业路径的重建保留原序号，因此先为新增链腾位；其他专业与前序段不变。
  for (const segment of next.segments) {
    if ((segment.majorId ?? null) === (original.majorId ?? null) && segment.routeSeq > original.routeSeq) {
      segment.routeSeq += chain.length - 1;
    }
  }
  chain.forEach((segment, index) => { segment.routeSeq = original.routeSeq + index; });
  next.segments.splice(next.segments.findIndex((segment) => segment.id === original.id), 1, ...chain);
  for (const day of next.days) for (const track of day.parallelTracks?.tracks ?? []) {
    const segmentIndex = track.segmentIds.indexOf(original.id);
    if (segmentIndex < 0) continue;
    const nodeIndex = track.nodeIds.indexOf(origin.id);
    if (day.id !== fromDay.id || fromDay.id !== toDay.id || track.nodeIds[nodeIndex + 1] !== destination.id) {
      throw new Error("中转所在专业路线已改变，请重新打开交通工作区");
    }
    track.nodeIds.splice(nodeIndex + 1, 0, ...inserted.map((node) => node.id));
    track.segmentIds.splice(segmentIndex, 1, ...chain.map((segment) => segment.id));
  }
  next.segments = rebuildAdjacentSegments(next.days, next.segments, createUuid);
  const retainedIds = new Set(next.segments.map((segment) => segment.id));
  if (chain.some((segment) => !retainedIds.has(segment.id))
    || editor.segments.some((segment) => segment.id !== original.id && !retainedIds.has(segment.id))) {
    throw new Error("中转插入会改变其他路线，请先调整相邻节点的位置");
  }
  const beforeIssues = new Set(validateEditor(editor).map((issue) => `${issue.code}|${issue.detail}`));
  const addedIssue = validateEditor(next).find((issue) => !beforeIssues.has(`${issue.code}|${issue.detail}`));
  if (addedIssue) throw new Error(addedIssue.detail);
  const group: RailwayTransferGroup = { version: 1, groupId: createUuid(),
    nodeIds: [origin.id, ...inserted.map((node) => node.id), destination.id],
    generatedNodeIds: inserted.map((node) => node.id), segmentIds: chain.map((segment) => segment.id),
    plans: structuredClone(alternatives), chainSignature: "" };
  group.chainSignature = chainSignature(next, group);
  for (const segment of next.segments) {
    if (group.segmentIds.includes(segment.id) && segment.transportMode === "rail" && segment.sourcePayload) {
      segment.sourcePayload.railwayTransfer = structuredClone(group);
    }
  }
  return next;
}
